import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import CategoryPicker from "./CategoryPicker";
import ImageUploader from "./ImageUploader";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { business_name: "", category: "", tagline: "", description: "", services: "",
  town: "", postcode: "", areas_covered: "", phone: "", email: "", website: "", photos: [] };

export default function ListingEditor({ listing, user, onSaved }) {
  const [cats, setCats] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setF(listing
      ? { ...EMPTY, ...Object.fromEntries(Object.entries(listing).map(([k, v]) => [k, v ?? ""])), services: (listing.services || []).join(", "), photos: listing.photos || [] }
      : { ...EMPTY, email: user?.email || "", phone: user?.phone || "" });
  }, [listing, user]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!f.category) return toast.error("Please choose a category");
    setSaving(true);
    try {
      const payload = {
        business_name: f.business_name, category: f.category, tagline: f.tagline || null,
        description: f.description, services: f.services.split(",").map((s) => s.trim()).filter(Boolean),
        town: f.town, postcode: f.postcode, areas_covered: f.areas_covered || null,
        phone: f.phone || null, email: f.email, website: f.website || null, photos: f.photos,
      };
      const { data } = await api.put("/me/listing", payload);
      toast.success(data.status === "rejected" ? "Changes saved — your listing is still taken down" : "Your listing is live");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="bg-white border border-zinc-200 rounded-lg p-6 md:p-8 space-y-5" data-testid="listing-editor">
      <div className="grid md:grid-cols-2 gap-4">
        <F label="Business name" v={f.business_name} on={set("business_name")} id="le-name" required />
        <div>
          <Label>Category</Label>
          <CategoryPicker value={f.category} onChange={(v) => setF((s) => ({ ...s, category: v }))}
            categories={cats} className="mt-1" testid="le-category" />
        </div>
      </div>
      <F label="Tagline (optional)" v={f.tagline} on={set("tagline")} id="le-tagline" placeholder="e.g. Friendly, patient instructor — manual & automatic" />
      <div>
        <Label htmlFor="le-desc">About your business</Label>
        <Textarea id="le-desc" rows={5} required value={f.description} onChange={set("description")} className="mt-1" data-testid="le-description" />
      </div>
      <F label="Services (comma separated)" v={f.services} on={set("services")} id="le-services" placeholder="Extensions, Loft conversions, Repairs" />
      <div className="grid md:grid-cols-3 gap-4">
        <F label="Town" v={f.town} on={set("town")} id="le-town" required />
        <F label="Postcode" v={f.postcode} on={set("postcode")} id="le-postcode" required />
        <F label="Areas covered (optional)" v={f.areas_covered} on={set("areas_covered")} id="le-areas" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <F label="Phone" v={f.phone} on={set("phone")} id="le-phone" />
        <F label="Email (enquiries go here)" v={f.email} on={set("email")} id="le-email" type="email" required />
        <F label="Website (optional)" v={f.website} on={set("website")} id="le-website" placeholder="https://" />
      </div>
      <div>
        <Label className="mb-2 block">Photos of your work</Label>
        <ImageUploader value={f.photos} onChange={(photos) => setF((s) => ({ ...s, photos }))} />
      </div>
      <Button type="submit" disabled={saving} className="bg-black text-white hover:bg-zinc-800" data-testid="le-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : listing ? "Save changes" : "Publish listing"}
      </Button>
    </form>
  );
}

function F({ label, v, on, id, type = "text", required, placeholder }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} required={required} value={v} onChange={on} placeholder={placeholder} className="mt-1" data-testid={id} />
    </div>
  );
}
